// AgentFormModal extracted from DeveloperPage.tsx
// Used by both AdminPage (admin mode) and DeveloperPage (creator mode)
import { useState, useRef, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { AGENT_NAME_PATTERN, AGENT_NAME_RULE } from '@ekkam/shared'
import {
  useCreateExternalAgentMutation,
  useUpdateExternalAgentMutation,
  useFinalizeAgentMutation,
  useDiscardDraftAgentMutation,
  useDeleteExternalAgentMutation,
  useResubmitAgentMutation,
  useLazyCheckAgentNameQuery,
  useGetKnowledgeFilesQuery,
  useDeleteKnowledgeFileMutation,
  useRegenerateSigningSecretMutation,
  useGetAgentStatsQuery,
  useGetAgentInvocationsQuery,
  useReplayInvocationMutation,
  useCheckAgentHealthMutation,
  useGetHostedProvidersQuery,
  type ExternalAgent,
} from '../../store/api/developerApi'
import { useUpdateAgentMutation } from '../../store/api/agentsApi'
import { api, ApiError } from '../../lib/apiClient'
import { ENDPOINTS } from '../../lib/endpoints'
import { useAppSelector } from '../../hooks/useAppDispatch'
import { CodeBlock, CopyButton, AgentStatusBanner } from '../../components/ui'
import { MultiSelectDropdown } from '../../components/ui/MultiSelectDropdown'
import { timeAgoShort } from '../../lib/avatar'
import s from '../../styles/pages/DeveloperPage.module.css'
import { SandboxDispatchPanel } from './SandboxTestPanel'
import { AgentSubscriptionModal } from './AgentSubscriptionModal'
import { AgentBillingPanel } from '../billing/AgentBillingPanel'
import {
  AGENT_DOC_MAX_FILES, AGENT_DOC_MAX_FILE_SIZE_BYTES, FILE_PROCESSING_POLL_MS,
  COPY_FEEDBACK_MS, CATEGORY_BADGE_PREVIEW_MAX, EMOJI_ICON_MAX_LEN,
  SANDBOX_RESPONSE_TIMEOUT_S,
} from '../../lib/config'

function prettyJson(str: string) { try { return JSON.stringify(JSON.parse(str), null, 2) } catch { return str } }

function getErrorExplanation(status: string, errorMessage: string | null): string | null {
  if (status === 'responded') return null
  if (status === 'timed_out') return `Your server did not call back within ${SANDBOX_RESPONSE_TIMEOUT_S}s. Return HTTP 200 immediately, then POST the response asynchronously.`
  if (!errorMessage) return null
  const msg = errorMessage.toLowerCase()
  if (msg.includes('401')) return 'Check HMAC-SHA256 signature verification with your signing secret.'
  if (msg.includes('403')) return 'Check your authentication logic.'
  if (msg.includes('404')) return 'Make sure your webhook route exists and the URL is correct.'
  if (msg.includes('405')) return 'Your endpoint must accept POST requests.'
  if (msg.includes('422') || msg.includes('400')) return 'Validation error — check you are reading the JSON body correctly.'
  if (msg.includes('500') || msg.includes('502') || msg.includes('503')) return 'Your server returned a 5xx. Check server logs.'
  if (msg.includes('connection') || msg.includes('econnrefused')) return 'Could not connect. Make sure your server is running and publicly reachable.'
  if (msg.includes('timeout') || msg.includes('abort')) return 'Request timed out. Your server may be overloaded or unreachable.'
  if (msg.includes('ssl') || msg.includes('certificate')) return 'SSL error. Make sure your endpoint has a valid certificate.'
  return null
}

// Re-export type so callers don't need to import from developerApi directly
export type { ExternalAgent }


function KnowledgeBaseSection({ agentId }: { agentId: string }) {
  const { data, isLoading, refetch } = useGetKnowledgeFilesQuery(agentId)
  const files = data?.files ?? []
  const [deleteFile] = useDeleteKnowledgeFileMutation()
  const { accessToken } = useAppSelector(st => st.auth)
  const [uploading, setUploading] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [uploadNotice, setUploadNotice] = useState<string | null>(null)
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false)
  // The file a 402 (subscription required) interrupted — replayed automatically
  // once payment goes through, so the creator doesn't have to pick it again.
  const [pendingFile, setPendingFile] = useState<File | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const MAX_FILES = AGENT_DOC_MAX_FILES

  useEffect(() => {
    if (files.some(f => f.status === 'processing')) {
      const t = setTimeout(() => void refetch(), FILE_PROCESSING_POLL_MS)
      return () => clearTimeout(t)
    }
  }, [files, refetch])

  const handleUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (fileInputRef.current) fileInputRef.current.value = ''
    setUploadNotice(null)
    if (file) void uploadFile(file)
  }

  const uploadFile = async (file: File) => {
    if (file.type !== 'application/pdf' && file.type !== 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') { setUploadError('Only PDF or Word (.docx) files are allowed'); return }
    if (file.size > AGENT_DOC_MAX_FILE_SIZE_BYTES) { setUploadError('File size exceeds 10MB'); return }
    if (files.some(f => f.filename.toLowerCase() === file.name.toLowerCase())) { setUploadError('A file with this name has already been uploaded'); return }
    setUploading(true); setUploadError(null)
    try {
      const formData = new FormData()
      formData.append('file', file)
      await api.upload('ai', ENDPOINTS.ai.knowledgeFiles(agentId), formData, accessToken)
      setPendingFile(null)
      setUploadNotice(null)
      void refetch()
    } catch (err: any) {
      // Backend requires an active subscription before it'll accept this
      // upload — stash the file, send the creator to the plan picker, and
      // leave the reason visible underneath so it's still there if they close
      // the modal without picking a plan. The upload replays after payment.
      if (err instanceof ApiError && err.status === 402) {
        setPendingFile(file)
        setUploadError(err.message)
        setShowSubscriptionModal(true)
      } else {
        setUploadError(err.message ?? 'Upload failed')
      }
    } finally {
      setUploading(false)
    }
  }

  const handleSubscriptionPaid = () => {
    setShowSubscriptionModal(false)
    setUploadError(null)
    const file = pendingFile
    if (file) {
      setUploadNotice('Payment received — finishing your upload…')
      void uploadFile(file)
    }
  }

  const handleDelete = async (fileId: string) => {
    if (!window.confirm('Remove this file from the knowledge base?')) return
    await deleteFile({ agentId, fileId }).unwrap().catch(() => {})
  }

  const fmtSize = (bytes: number) => bytes < 1024 * 1024 ? `${(bytes / 1024).toFixed(0)}KB` : `${(bytes / (1024 * 1024)).toFixed(1)}MB`
  const statusColor: Record<string, string> = { ready: 'var(--success)', processing: 'var(--warning)', failed: 'var(--error)' }

  return (
    <div style={{ padding: '1.25rem 1.5rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
        <div>
          <span style={{ fontWeight: 700, fontSize: '0.88rem', color: 'var(--text-primary)' }}>📚 Knowledge Base</span>
          <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginLeft: '0.5rem' }}>{files.length}/{MAX_FILES} files</span>
        </div>
        {files.length < MAX_FILES && (
          <>
            <input ref={fileInputRef} type="file" accept="application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document" onChange={handleUpload} style={{ display: 'none' }} />
            <button onClick={() => fileInputRef.current?.click()} disabled={uploading} className={s.primaryBtn} style={{ fontSize: '0.75rem', padding: '0.3rem 0.75rem' }}>
              {uploading ? '⏳ Uploading...' : '+ Upload PDF / Word'}
            </button>
          </>
        )}
      </div>
      {uploadError && <div style={{ fontSize: '0.75rem', color: 'var(--error)', marginBottom: '0.5rem' }}>{uploadError}</div>}
      {uploadNotice && <div style={{ fontSize: '0.75rem', color: 'var(--success)', marginBottom: '0.5rem' }}>{uploadNotice}</div>}
      <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginBottom: '0.9rem', lineHeight: 1.5 }}>
        Upload PDFs or Word (.docx) files to give this agent domain-specific knowledge. Max 5 files, 10MB each.
      </div>
      {isLoading && <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>Loading...</div>}
      {!isLoading && files.length === 0 && <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>No files uploaded yet.</div>}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
        {files.map(file => (
          <div key={file.id} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.5rem 0.75rem', background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 8 }}>
            <span style={{ fontSize: '1rem' }}>📄</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{file.filename}</div>
              <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)', marginTop: '0.1rem' }}>
                {fmtSize(file.fileSizeBytes)}
                {file.status === 'ready' && <span> · {file.chunkCount} chunks</span>}
                {file.errorMessage && <span style={{ color: 'var(--error)' }}> · {file.errorMessage}</span>}
              </div>
            </div>
            <span style={{ fontSize: '0.68rem', fontWeight: 700, color: statusColor[file.status] ?? 'var(--text-muted)', whiteSpace: 'nowrap' }}>
              {file.status === 'ready' ? '✓ Ready' : file.status === 'processing' ? '⏳ Processing' : '✗ Failed'}
            </span>
            <button onClick={() => void handleDelete(file.id)} style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 5, color: 'var(--error)', fontSize: '0.68rem', padding: '0.2rem 0.45rem', cursor: 'pointer', flexShrink: 0 }}>×</button>
          </div>
        ))}
      </div>

      {showSubscriptionModal && (
        <AgentSubscriptionModal
          agentId={agentId}
          onClose={() => setShowSubscriptionModal(false)}
          onPaid={handleSubscriptionPaid}
        />
      )}
    </div>
  )
}

// ── Sandbox tab: signing secret (shown once, or regenerate) + full
// round-trip test against the developer's own webhook. ──
function SandboxTab({ agentId, endpoint, signingSecret, apiKeyPrefix }: {
  agentId: string
  endpoint: string | null
  signingSecret: string | null
  apiKeyPrefix: string | null
}) {
  const [localSecret, setLocalSecret] = useState(signingSecret)
  const [copied, setCopied] = useState(false)
  const [regenerateSecret, { isLoading: regenerating }] = useRegenerateSigningSecretMutation()
  const [checkHealth, { data: healthData, isLoading: healthLoading }] = useCheckAgentHealthMutation()

  useEffect(() => { void checkHealth(agentId) }, [agentId])

  const handleCopy = () => {
    if (!localSecret) return
    void navigator.clipboard.writeText(localSecret)
    setCopied(true)
    setTimeout(() => setCopied(false), COPY_FEEDBACK_MS)
  }

  const handleRegenerate = async () => {
    if (!window.confirm(
      "Regenerate this agent's signing secret? The old secret stops working immediately — your server's signature check will fail until you update it with the new one."
    )) return
    try {
      const res = await regenerateSecret(agentId).unwrap()
      setLocalSecret(res.signingSecret)
      setCopied(false)
    } catch { /* button stays clickable to retry */ }
  }

  return (
    <div style={{ padding: '1.25rem 1.5rem', display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
      {endpoint && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '0.75rem', fontWeight: 600, color: healthLoading ? 'var(--text-muted)' : healthData?.reachable ? 'var(--success)' : 'var(--error)' }}>
            {healthLoading ? '● Checking…' : healthData?.reachable ? `● Online (${healthData.latencyMs}ms)` : '● Unreachable'}
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 7, padding: '0.25rem 0.6rem' }}>
            <span style={{ fontSize: '0.7rem' }}>🔗</span>
            <code style={{ fontSize: '0.72rem', color: 'var(--text-secondary)' }}>{endpoint}</code>
            <CopyButton text={endpoint} small />
          </div>
          <button onClick={() => void checkHealth(agentId)} disabled={healthLoading} className={s.ghostBtnSm} style={{ fontSize: '0.68rem' }}>↺ Re-check</button>
        </div>
      )}

      <div>
        <div style={{ fontSize: '0.7rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.6rem' }}>🔑 Signing Secret</div>
        {localSecret ? (
          <>
            <p style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', margin: '0 0 0.6rem' }}>
              Shown <strong>once only</strong>. Use it to verify incoming webhooks from Ekkam.
            </p>
            <div className={s.secretBox} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <code style={{ flex: 1, wordBreak: 'break-all', fontSize: '0.72rem' }}>{localSecret}</code>
              <button onClick={handleCopy} className={s.ghostBtnSm} style={{ flexShrink: 0 }}>
                {copied ? '✓ Copied' : 'Copy'}
              </button>
            </div>
          </>
        ) : (
          <>
            <p style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', margin: '0 0 0.6rem', lineHeight: 1.5 }}>
              It was shown once already, right when this agent was created. If you saved it, you're all set. If you didn't, generate a new one — the old one stops working immediately.
            </p>
            <button onClick={() => void handleRegenerate()} disabled={regenerating} className={s.ghostBtnSm} style={{ fontSize: '0.75rem' }}>
              {regenerating ? '⏳ Regenerating…' : '🔁 Regenerate secret'}
            </button>
          </>
        )}
      </div>

      <div>
        <div style={{ fontSize: '0.7rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.6rem' }}>🧪 Test Your Integration</div>
        <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', margin: '0 0 0.6rem' }}>
          Sends a real sample question to your webhook and checks whether your server calls back with a usable answer.
        </p>
        <SandboxDispatchPanel agentId={agentId} apiKeyPrefix={apiKeyPrefix} />
      </div>
    </div>
  )
}

// ── Invocations tab: history of every dispatch to this agent, with replay. ──
function InvocationsTab({ agentId }: { agentId: string }) {
  const { data, isLoading, refetch } = useGetAgentInvocationsQuery({ agentId, limit: 50 })
  const invocations = data?.invocations ?? []
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [replayingId, setReplayingId] = useState<string | null>(null)
  const [replayResults, setReplayResults] = useState<Record<string, { success: boolean; statusCode: number; responseMs: number; error?: string }>>({})
  const [replay] = useReplayInvocationMutation()

  const statusColor: Record<string, string> = { responded: 'var(--success)', pending: 'var(--warning)', timed_out: 'var(--error)', failed: 'var(--error)' }

  const handleReplay = async (invocationId: string) => {
    setReplayingId(invocationId)
    try {
      const res = await replay({ agentId, invocationId }).unwrap()
      setReplayResults(prev => ({ ...prev, [invocationId]: res }))
      void refetch()
    } catch (err: any) {
      setReplayResults(prev => ({ ...prev, [invocationId]: { success: false, statusCode: 0, responseMs: 0, error: err?.data?.message ?? 'Replay failed' } }))
    } finally { setReplayingId(null) }
  }

  return (
    <div style={{ padding: '1.25rem 1.5rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.85rem' }}>
        <span style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          {invocations.length} invocations
        </span>
        <button onClick={() => void refetch()} style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text-muted)', fontSize: '0.7rem', padding: '0.25rem 0.6rem', cursor: 'pointer' }}>↺ Refresh</button>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
        {isLoading && <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.8rem' }}>Loading...</div>}
        {!isLoading && invocations.length === 0 && (
          <div style={{ padding: '4rem 1rem', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.82rem', lineHeight: 1.7 }}>
            No invocations yet.<br />Run Sandbox or post a matching discussion to trigger your agent.
          </div>
        )}
        {invocations.map(inv => {
          const isExpanded = expandedId === inv.id
          const hasPayload = !!(inv.requestPayload || inv.responseBody !== null)
          const explanation = getErrorExplanation(inv.status, inv.errorMessage)
          return (
            <div key={inv.id} style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 9, padding: '0.7rem 0.85rem', display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.55rem' }}>
                <span style={{ width: 7, height: 7, borderRadius: '50%', background: statusColor[inv.status] ?? '#888', flexShrink: 0, marginTop: 5 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  {inv.isSandbox && <span style={{ fontSize: '0.62rem', fontWeight: 700, color: 'var(--warning)', background: 'rgba(234,179,8,0.1)', padding: '0.1rem 0.3rem', borderRadius: 3, marginRight: '0.3rem' }}>SANDBOX</span>}
                  {inv.discussionTitle && !inv.isSandbox
                    ? <Link to={`/discussions/${inv.discussionId}`} style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-primary)', textDecoration: 'none' }}>{inv.discussionTitle}</Link>
                    : <span style={{ fontSize: '0.8rem', fontWeight: 600, color: inv.isSandbox ? 'var(--text-primary)' : 'var(--text-muted)' }}>
                        {inv.isSandbox ? 'Sandbox dispatch' : 'Unknown discussion'}
                      </span>
                  }
                </div>
                <div style={{ display: 'flex', gap: '0.3rem', flexShrink: 0 }}>
                  {hasPayload && (
                    <button onClick={() => setExpandedId(isExpanded ? null : inv.id)} style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 5, color: 'var(--text-muted)', fontSize: '0.65rem', padding: '0.2rem 0.5rem', cursor: 'pointer' }}>
                      {isExpanded ? '▲' : '🔍'}
                    </button>
                  )}
                  {inv.requestPayload && (
                    <button onClick={() => void handleReplay(inv.id)} disabled={replayingId === inv.id} style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 5, color: 'var(--accent-primary)', fontSize: '0.65rem', padding: '0.2rem 0.5rem', cursor: 'pointer' }}>
                      {replayingId === inv.id ? '⏳' : '↺ Replay'}
                    </button>
                  )}
                </div>
              </div>

              <div style={{ display: 'flex', gap: '0.5rem', fontSize: '0.7rem', color: 'var(--text-muted)', paddingLeft: '1.2rem', flexWrap: 'wrap' }}>
                <span style={{ color: statusColor[inv.status] ?? '#888', fontWeight: 600 }}>{inv.status}</span>
                {inv.responseMs != null && <span>· {inv.responseMs}ms</span>}
                <span>· {timeAgoShort(inv.requestedAt)}</span>
                {inv.errorMessage && <span style={{ color: 'var(--error)' }}>· {inv.errorMessage}</span>}
              </div>

              {explanation && (
                <div style={{ marginLeft: '1.2rem', fontSize: '0.7rem', color: 'var(--warning)', background: 'rgba(234,179,8,0.06)', border: '1px solid rgba(234,179,8,0.15)', borderRadius: 6, padding: '0.35rem 0.6rem', lineHeight: 1.5 }}>
                  💡 {explanation}
                </div>
              )}

              {replayResults[inv.id] && (
                <div style={{ marginLeft: '1.2rem', fontSize: '0.7rem', padding: '0.3rem 0.55rem', borderRadius: 5, border: `1px solid ${replayResults[inv.id].success ? 'rgba(34,197,94,0.25)' : 'rgba(239,68,68,0.25)'}`, background: replayResults[inv.id].success ? 'rgba(34,197,94,0.05)' : 'rgba(239,68,68,0.05)' }}>
                  <span style={{ fontWeight: 700, color: replayResults[inv.id].success ? 'var(--success)' : 'var(--error)' }}>
                    Replay: {replayResults[inv.id].success ? `✅ HTTP ${replayResults[inv.id].statusCode}` : `❌ ${replayResults[inv.id].error ?? `HTTP ${replayResults[inv.id].statusCode}`}`}
                  </span>
                  <span style={{ color: 'var(--text-muted)', marginLeft: '0.4rem' }}>{replayResults[inv.id].responseMs}ms</span>
                </div>
              )}

              {isExpanded && (
                <div style={{ marginLeft: '1.2rem', display: 'flex', flexDirection: 'column', gap: '0.5rem', marginTop: '0.25rem' }}>
                  {inv.requestPayload && (
                    <div>
                      <div style={{ fontSize: '0.65rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.25rem' }}>Payload sent</div>
                      <CodeBlock maxHeight={200}>{prettyJson(inv.requestPayload)}</CodeBlock>
                    </div>
                  )}
                  {inv.responseBody !== null && (
                    <div>
                      <div style={{ fontSize: '0.65rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.25rem' }}>Response body</div>
                      <CodeBlock maxHeight={200}>{inv.responseBody ? prettyJson(inv.responseBody) : '(empty)'}</CodeBlock>
                    </div>
                  )}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ── Stats strip: compact performance summary shown above the tabs. ──
function StatsStrip({ agentId }: { agentId: string }) {
  const { data } = useGetAgentStatsQuery(agentId)
  const stats = data?.stats
  if (!stats) return null
  const items = [
    { label: 'Invocations', value: stats.invocations.total },
    { label: 'Response Rate', value: stats.invocations.responseRate !== null ? `${stats.invocations.responseRate}%` : '—', color: stats.invocations.responseRate !== null ? (stats.invocations.responseRate >= 80 ? 'var(--success)' : stats.invocations.responseRate >= 50 ? 'var(--warning)' : 'var(--error)') : undefined },
    { label: 'Avg Response', value: stats.invocations.avgResponseMs ? `${stats.invocations.avgResponseMs}ms` : '—' },
    { label: 'Leaderboard', value: stats.leaderboard ? `#${stats.leaderboard.rank}` : '—' },
  ]
  return (
    <div style={{ display: 'flex', gap: '0.5rem', padding: '0.75rem 1.5rem', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
      {items.map(it => (
        <div key={it.label} style={{ flex: 1, textAlign: 'center', padding: '0.35rem 0.5rem', background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 8 }}>
          <div style={{ fontSize: '0.95rem', fontWeight: 800, color: it.color ?? 'var(--text-primary)' }}>{it.value}</div>
          <div style={{ fontSize: '0.6rem', color: 'var(--text-muted)', marginTop: '0.1rem', whiteSpace: 'nowrap' }}>{it.label}</div>
        </div>
      ))}
    </div>
  )
}

export function AgentFormModal({
  existing, categories, onClose, isAdmin = false, apiKeyPrefix = null,
}: {
  existing?: ExternalAgent | any  // `any` covers AgentAdminRecord passed from AdminAgents
  categories: { id: string; name: string; icon: string | null; color: string | null }[]
  onClose: () => void
  isAdmin?: boolean
  apiKeyPrefix?: string | null
}) {
  const [agentType, setAgentType] = useState<'external' | 'hosted'>(
    existing?.agentType === 'hosted' ? 'hosted' :
    existing?.agentType === 'external' ? 'external' :
    'hosted'
  )
  const [name, setName]         = useState(existing?.name ?? '')
  const [desc, setDesc]         = useState(existing?.description ?? '')
  const [icon, setIcon]         = useState(existing?.icon ?? '')
  const [color, setColor]       = useState(existing?.color ?? '#7c3aed')
  const [endpoint, setEndpoint] = useState(existing?.externalEndpoint ?? '')
  const [catIds, setCatIds]         = useState<string[]>(existing?.categoryIds ?? [])
  const [suggestedCatName, setSuggestedCatName] = useState(existing?.suggestedCategoryName ?? '')
  const { data: providersData } = useGetHostedProvidersQuery()
  const providers = providersData?.providers ?? {}

  const [hostedProvider, setHostedProvider] = useState(
    existing?.hostedProvider ?? (isAdmin ? (existing as any)?.provider : undefined) ?? 'groq'
  )
  const [hostedModel, setHostedModel] = useState(
    existing?.hostedModel ?? (isAdmin ? (existing as any)?.model : undefined) ?? ''
  )
  const [systemPrompt, setSystemPrompt]     = useState(existing?.systemPrompt ?? '')
  const [apiKey, setApiKey]     = useState('')
  const [replaceKey, setReplaceKey] = useState(false)
  const [useOwnKey, setUseOwnKey]   = useState(existing?.hasApiKey === true)

  // When providers load and no model is set yet, default to the first model of the selected provider
  useEffect(() => {
    if (!hostedModel && hostedProvider && providers[hostedProvider]?.models?.length) {
      setHostedModel(providers[hostedProvider].models[0])
    }
  }, [providers, hostedProvider])

  // Set the moment a brand-new agent is created — from then on `agentRecord`
  // (below) treats it exactly like `existing`, so the same tabs/actions apply
  // whether you're finishing a fresh registration or reopening a draft later.
  const [createdAgent, setCreatedAgent] = useState<{ id: string; agentType: 'external' | 'hosted'; status: string; signingSecret: string | null } | null>(null)
  const [activeTab, setActiveTab] = useState<'details' | 'knowledge' | 'billing' | 'sandbox' | 'invocations'>('details')
  const [nameError, setNameError]     = useState<string | null>(null)
  const [descError, setDescError]     = useState<string | null>(null)
  const [promptError, setPromptError] = useState<string | null>(null)
  const [formError, setFormError]     = useState<string | null>(null)
  const [saved, setSaved]             = useState(false)
  const [nameAvailable, setNameAvailable] = useState<boolean | null>(null)
  const [checkingName, setCheckingName]   = useState(false)
  const [checkName]    = useLazyCheckAgentNameQuery()
  const [create, { isLoading: creating }]       = useCreateExternalAgentMutation()
  const [update, { isLoading: updating }]       = useUpdateExternalAgentMutation()
  const [updateAdmin, { isLoading: updatingAdmin }] = useUpdateAgentMutation()
  const [finalize, { isLoading: finalizing }] = useFinalizeAgentMutation()
  const [discard, { isLoading: discarding }] = useDiscardDraftAgentMutation()
  const [deactivate, { isLoading: deactivating }] = useDeleteExternalAgentMutation()
  const [resubmit, { isLoading: resubmitting }] = useResubmitAgentMutation()
  const isLoading = creating || updating || updatingAdmin

  // Unified reference to "the agent we're working with" — null until the
  // Details tab has been saved at least once (brand-new registration).
  const agentRecord: { id: string; status: string; reviewNote?: string | null } | null = existing ?? createdAgent
  const isDraftRecord = agentRecord?.status === 'draft'
  const canDeactivate = agentRecord && (agentRecord.status === 'active' || agentRecord.status === 'pending_review')
  const canResubmit = agentRecord && (agentRecord.status === 'rejected' || agentRecord.status === 'inactive')

  // Hosted agents need at least one processed document before submitting;
  // external agents answer from their own server, so it's optional for them.
  const { data: kbData } = useGetKnowledgeFilesQuery(agentRecord?.id ?? '', { skip: !agentRecord || agentType !== 'hosted' })
  const readyCount = (kbData?.files ?? []).filter(f => f.status === 'ready').length
  const hasReady = agentType === 'external' || readyCount > 0

  // Dirty tracking for an existing agent's Details tab — Save Changes stays
  // disabled once saved and only re-enables when a field actually changes
  // again, instead of inviting a pointless re-save of identical values.
  const [dirty, setDirty] = useState(false)
  const skipNextDirtyRef = useRef(true)

  // New baseline whenever the underlying record changes — a brand-new agent
  // was just created, or a different existing agent loaded into this modal.
  useEffect(() => {
    skipNextDirtyRef.current = true
    setDirty(false)
  }, [agentRecord?.id])

  useEffect(() => {
    if (skipNextDirtyRef.current) { skipNextDirtyRef.current = false; return }
    setDirty(true)
  }, [name, desc, icon, color, catIds, suggestedCatName, agentType, endpoint, hostedProvider, hostedModel, systemPrompt, useOwnKey, apiKey, replaceKey])

  const tabs = [
    { key: 'details' as const, label: '📝 Details' },
    ...(agentRecord && agentType === 'hosted'   ? [{ key: 'knowledge' as const, label: '📚 Knowledge Base' }] : []),
    ...(agentRecord ? [{ key: 'billing' as const, label: '🧾 Billing' }] : []),
    ...(agentRecord && agentType === 'external' ? [{ key: 'sandbox' as const,   label: '🧪 Sandbox' }] : []),
    ...(agentRecord && agentType === 'external' ? [{ key: 'invocations' as const, label: '📋 Invocations' }] : []),
  ]

  // Checked as you type rather than on blur — the rule is about which
  // characters are allowed, so there's no reason to make the user leave the
  // field to find out. Backed by the same pattern the API validates against.
  const nameFormatError = name.trim() && !AGENT_NAME_PATTERN.test(name.trim())
    ? AGENT_NAME_RULE
    : null

  const handleNameBlur = async () => {
    const trimmed = name.trim()
    if (!trimmed || nameFormatError || (existing && trimmed === existing.name)) return
    setCheckingName(true); setNameAvailable(null); setNameError(null)
    try {
      const res = await checkName(trimmed).unwrap()
      setNameAvailable(res.available)
      if (!res.available) setNameError(`"${trimmed}" is already taken. Please choose a different name.`)
    } catch { setNameAvailable(null) }
    finally { setCheckingName(false) }
  }

  const handleSave = async () => {
    if (!name.trim() || nameFormatError) return
    if (agentType === 'external' && !endpoint.trim()) return
    let valid = true
    if (!desc.trim()) { setDescError('Description is required.'); valid = false } else setDescError(null)
    if (agentType === 'hosted' && !systemPrompt.trim()) { setPromptError('System prompt is required for hosted agents.'); valid = false } else setPromptError(null)
    if (!valid) return
    setNameError(null); setFormError(null); setSaved(false)
    try {
      if (isAdmin && existing) {
        // Admin editing an internal/hosted agent
        await updateAdmin({
          id: existing.id,
          body: {
            name: name.trim(), description: desc.trim() || undefined,
            icon: icon.trim() || undefined, color: color.trim() || undefined,
            categoryIds: catIds,
            ...(agentType === 'hosted' ? { hostedProvider, hostedModel, systemPrompt: systemPrompt.trim() || undefined, ...(useOwnKey && (replaceKey || !existing.hasApiKey) && apiKey.trim() ? { apiKey: apiKey.trim() } : {}) } : {}),
          },
        }).unwrap()
        onClose()
        return
      }
      if (agentRecord) {
        await update({
          id: agentRecord.id,
          body: {
            name: name.trim(), description: desc.trim() || undefined,
            icon: icon.trim() || undefined, color: color.trim() || undefined,
            externalEndpoint: agentType === 'external' ? endpoint.trim() : undefined,
            categoryIds: catIds,
            suggestedCategoryName: suggestedCatName.trim() || null,
            ...(agentType === 'hosted' ? { hostedProvider, hostedModel, systemPrompt: systemPrompt.trim() || undefined, ...(useOwnKey && (replaceKey || !existing?.hasApiKey) && apiKey.trim() ? { apiKey: apiKey.trim() } : {}) } : {}),
          },
        }).unwrap()
        // Nothing else changes the UI on success (no navigation, no tab
        // switch like the create/admin branches below) — without this the
        // save silently succeeds and the modal just sits there looking like
        // nothing happened.
        setSaved(true)
        setDirty(false)
        setTimeout(() => setSaved(false), 3000)
      } else {
        const res = await create({
          name: name.trim(), description: desc.trim() || undefined,
          icon: icon.trim() || undefined, color: color.trim() || undefined,
          agentType,
          externalEndpoint: agentType === 'external' ? endpoint.trim() : undefined,
          categoryIds: catIds,
          suggestedCategoryName: suggestedCatName.trim() || null,
          ...(agentType === 'hosted' ? { hostedProvider, hostedModel, systemPrompt: systemPrompt.trim() || undefined, useOwnKey, ...(useOwnKey && apiKey.trim() ? { apiKey: apiKey.trim() } : {}) } : {}),
        }).unwrap()
        setCreatedAgent({ id: res.agent.id, agentType, status: 'draft', signingSecret: res.agent.signingSecret ?? null })
        // Jump straight to whichever tab matters next for this agent type.
        setActiveTab(agentType === 'hosted' ? 'knowledge' : 'sandbox')
      }
    } catch (err: any) {
      setFormError(err?.data?.message ?? err?.message ?? 'Save failed')
    }
  }

  const handleFinalize = async () => {
    if (!agentRecord) return
    setFormError(null)
    try {
      await finalize(agentRecord.id).unwrap()
      onClose()
    } catch (err: any) {
      setFormError(err?.data?.message ?? err?.message ?? 'Could not submit the agent. Please try again.')
    }
  }

  const handleDiscard = async () => {
    if (!agentRecord) return
    if (!window.confirm('Discard this agent? The agent and any uploaded documents will be permanently deleted.')) return
    try { await discard(agentRecord.id).unwrap() } catch { /* fall through to close anyway */ }
    onClose()
  }

  const handleDeactivate = async () => {
    if (!agentRecord) return
    const verb = agentRecord.status === 'active' ? 'Deactivate' : 'Withdraw'
    if (!window.confirm(`${verb} "${name}"? It will stop receiving discussion webhooks.`)) return
    try { await deactivate(agentRecord.id).unwrap() } catch { /* fall through to close anyway */ }
    onClose()
  }

  const handleResubmit = async () => {
    if (!agentRecord) return
    try { await resubmit(agentRecord.id).unwrap() } catch { /* ignore — error state not critical here */ }
  }

  return (
    <div className={s.modalOverlay} onClick={e => e.target === e.currentTarget && onClose()}>
      {/* Fixed height (not just max-height) so the modal doesn't resize as you
          switch tabs or as the invocation list grows — content scrolls inside
          instead. */}
      <div className={s.modal} style={{ maxWidth: 860, height: 'min(680px, 90vh)' }}>
        <div className={s.modalHeader}>
          <h3 className={s.modalTitle}>{existing ? existing.name : (createdAgent ? name : 'Register Agent')}</h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            {isDraftRecord && (
              <button
                onClick={() => void handleDiscard()}
                className={s.dangerBtn}
                style={{ padding: '0.25rem 0.5rem', fontSize: '0.68rem' }}
                title="Discard draft"
                disabled={discarding || finalizing}
              >
                {discarding ? '…' : '🗑️'}
              </button>
            )}
            {canDeactivate && (
              <button
                onClick={() => void handleDeactivate()}
                className={s.dangerBtn}
                style={{ padding: '0.25rem 0.5rem', fontSize: '0.68rem' }}
                title={agentRecord!.status === 'active' ? 'Deactivate' : 'Withdraw'}
                disabled={deactivating}
              >
                {deactivating ? '…' : '✕'}
              </button>
            )}
            <button onClick={onClose} className={s.closeBtn}>✕</button>
          </div>
        </div>

        {agentRecord && (
          <AgentStatusBanner
            status={agentRecord.status}
            agentType={agentType}
            reviewNote={agentRecord.reviewNote}
            style={{ margin: '0.75rem 1.5rem 0', flexShrink: 0 }}
          />
        )}

        {agentRecord && <StatsStrip agentId={agentRecord.id} />}

        {tabs.length > 1 && (
          <div style={{ display: 'flex', gap: '0.25rem', padding: '0 1.5rem', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
            {tabs.map(t => (
              <button
                key={t.key}
                onClick={() => setActiveTab(t.key)}
                style={{
                  padding: '0.7rem 0.9rem', background: 'none', border: 'none', cursor: 'pointer',
                  borderBottom: activeTab === t.key ? '2px solid var(--accent-primary)' : '2px solid transparent',
                  color: activeTab === t.key ? 'var(--text-primary)' : 'var(--text-muted)',
                  fontWeight: 700, fontSize: '0.82rem', marginBottom: '-1px',
                }}
              >
                {t.label}
              </button>
            ))}
          </div>
        )}

        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
          {activeTab === 'details' && (
            <div className={s.modalBody} style={{ maxHeight: 'none' }}>
              {/* ── LEFT: Identity ── */}
              <div className={s.modalCol}>
                <div style={{ fontSize: '0.7rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Identity</div>

                <div className={s.formField}>
                  <label>Name *</label>
                  <input
                    value={name}
                    onChange={e => { setName(e.target.value); setNameError(null); setNameAvailable(null) }}
                    onBlur={() => void handleNameBlur()}
                    placeholder="ChefBot, TaxGuru, WellnessAI..."
                    className={s.input}
                    style={{ borderColor: nameError || nameFormatError ? 'var(--error)' : nameAvailable === true ? 'var(--success)' : undefined }}
                  />
                  {nameFormatError && <span style={{ fontSize: '0.72rem', color: 'var(--error)' }}>{nameFormatError}</span>}
                  {checkingName && <span className={s.fieldHint}>Checking availability…</span>}
                  {nameError && !nameFormatError && <span style={{ fontSize: '0.72rem', color: 'var(--error)' }}>{nameError}</span>}
                  {nameAvailable === true && !nameError && !nameFormatError && <span style={{ fontSize: '0.72rem', color: 'var(--success)' }}>✓ Name available</span>}
                </div>

                <div className={s.formField}>
                  <label>Description *</label>
                  <textarea
                    value={desc}
                    onChange={e => { setDesc(e.target.value); if (e.target.value.trim()) setDescError(null) }}
                    placeholder="Helps users with recipes and cooking tips..."
                    className={s.input}
                    rows={2}
                    style={{ resize: 'vertical', borderColor: descError ? 'var(--error)' : undefined }}
                  />
                  {descError && <span style={{ fontSize: '0.72rem', color: 'var(--error)' }}>{descError}</span>}
                </div>

                <div style={{ display: 'flex', gap: '0.75rem' }}>
                  <div className={s.formField} style={{ flex: 1 }}>
                    <label>Icon (emoji)</label>
                    <input value={icon} onChange={e => setIcon(e.target.value)} placeholder="🍽️" maxLength={EMOJI_ICON_MAX_LEN} className={s.input} />
                  </div>
                  <div className={s.formField} style={{ flex: 1 }}>
                    <label>Color</label>
                    <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
                      <input value={color} onChange={e => setColor(e.target.value)} placeholder="#7c3aed" className={s.input} style={{ flex: 1 }} />
                      <input
                        type="color"
                        value={/^#[0-9a-fA-F]{6}$/.test(color) ? color : '#7c3aed'}
                        onChange={e => setColor(e.target.value)}
                        title="Pick a color"
                        style={{ width: 32, height: 32, padding: 0, border: '1px solid var(--border)', borderRadius: 6, background: 'none', cursor: 'pointer', flexShrink: 0 }}
                      />
                    </div>
                  </div>
                </div>

                <div className={s.formField}>
                  <label>Category</label>
                  <MultiSelectDropdown
                    options={categories.map(c => ({ value: c.id, label: c.name, icon: c.icon ?? undefined, color: c.color ?? undefined }))}
                    selected={catIds}
                    onChange={setCatIds}
                    placeholder="Select a category"
                    searchPlaceholder="Search categories..."
                    align="left"
                    fullWidth
                  />
                  <span className={s.fieldHint}>Leave empty for General (responds to all discussions)</span>
                </div>

                <div className={s.formField}>
                  <label>Suggest a category <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(optional)</span></label>
                  <input
                    value={suggestedCatName}
                    onChange={e => setSuggestedCatName(e.target.value)}
                    placeholder="e.g. DeFi Tools, Career Coaching…"
                    className={s.input}
                  />
                  {agentRecord && existing?.suggestedCategoryName && (
                    <div style={{ marginTop: '0.35rem', fontSize: '0.72rem', color: 'var(--text-muted)', background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 6, padding: '0.35rem 0.6rem' }}>
                      💡 Creator suggested: <strong>"{existing.suggestedCategoryName}"</strong>
                      {isAdmin && <span> — create it in Categories admin if needed, then assign above</span>}
                    </div>
                  )}
                  <span className={s.fieldHint}>Don't see a fit? Suggest a new category for admins to review.</span>
                </div>
              </div>

              {/* ── RIGHT: Configuration ── */}
              <div className={s.modalCol}>
                <div style={{ fontSize: '0.7rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Configuration</div>

                {(!agentRecord || isAdmin) && (
                  <div className={s.formField}>
                    <label>Agent Type *</label>
                    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: '0.5rem' }}>
                      {(['hosted', 'external'] as const).map(t => {
                        const active = agentType === t
                        return (
                          <button
                            key={t}
                            onClick={() => setAgentType(t)}
                            style={{
                              width: '100%',
                              padding: '0.55rem 0.5rem',
                              borderRadius: 8,
                              border: active ? '1px solid var(--accent-primary)' : '1px solid var(--border)',
                              background: active ? 'var(--accent-primary)' : 'transparent',
                              color: active ? 'white' : 'var(--text-secondary)',
                              fontSize: '0.82rem',
                              fontWeight: 600,
                              cursor: 'pointer',
                              textAlign: 'center',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            {t === 'hosted' ? '☁️ Hosted (Ekkam AI)' : '🔗 External (Webhook)'}
                          </button>
                        )
                      })}
                    </div>
                    <span className={s.fieldHint}>
                      {agentType === 'external' ? 'Ekkam calls your server. You handle AI inference.' : "Ekkam runs inference for you. No server needed."}
                    </span>
                  </div>
                )}

                {agentType === 'external' && (
                  <div className={s.formField}>
                    <label>Webhook Endpoint URL *</label>
                    <input value={endpoint} onChange={e => { setEndpoint(e.target.value); setFormError(null) }} placeholder="https://your-api.com/ekkam/webhook" className={s.input} style={{ borderColor: formError && (formError.toLowerCase().includes('endpoint') || formError.toLowerCase().includes('url')) ? 'var(--error)' : undefined }} />
                    <span className={s.fieldHint}>
                      Ekkam will POST discussion details here. You have 30 seconds to respond. To charge for a specific
                      response, include <code>isPremium: true</code> and <code>premiumAmountPaise</code> when you call back
                      {' '}<code>POST /api/v1/agent-response</code> — decided per response, no setup needed here.
                    </span>
                    {formError && (formError.toLowerCase().includes('endpoint') || formError.toLowerCase().includes('url')) && <span style={{ fontSize: '0.72rem', color: 'var(--error)' }}>{formError}</span>}
                  </div>
                )}

                {agentType === 'hosted' && (
                  <>
                    <div className={s.formField}>
                      <label>Hosting Mode *</label>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                        {([
                          { value: false, label: '🌐 Use Ekkam Token', desc: 'Ekkam handles inference. Usage is billed to your Ekkam account.', locked: false },
                          // Bringing your own key isn't wired up on the backend yet — locked
                          // for new agents, but left usable for any existing agent already
                          // configured this way so it doesn't silently break for them.
                          { value: true,  label: '🔑 Bring Your Own Key', desc: 'Use your own Groq/Gemini/OpenAI key. No Ekkam billing.', locked: !(existing?.hasApiKey) },
                        ] as const).map(opt => (
                          <label
                            key={String(opt.value)}
                            style={{
                              display: 'flex', alignItems: 'flex-start', gap: '0.65rem', padding: '0.6rem 0.85rem', borderRadius: 8, transition: 'all 0.12s',
                              border: `1px solid ${useOwnKey === opt.value ? 'var(--accent-primary)' : 'var(--border)'}`,
                              background: useOwnKey === opt.value ? 'rgba(124,58,237,0.08)' : 'var(--bg-elevated)',
                              cursor: opt.locked ? 'not-allowed' : 'pointer',
                              opacity: opt.locked ? 0.55 : 1,
                            }}
                          >
                            <input
                              type="radio"
                              name="hostingMode"
                              checked={useOwnKey === opt.value}
                              disabled={opt.locked}
                              onChange={() => { if (!opt.locked) setUseOwnKey(opt.value) }}
                              style={{ marginTop: 3, accentColor: 'var(--accent-primary)', flexShrink: 0 }}
                            />
                            <div>
                              <div style={{ fontWeight: 600, fontSize: '0.82rem', color: 'var(--text-primary)' }}>{opt.label}{opt.locked ? ' 🔒 (coming soon)' : ''}</div>
                              <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: '0.15rem' }}>{opt.desc}</div>
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>
                    <div className={s.formField}>
                      <label>AI Provider *</label>
                      <select
                        value={hostedProvider}
                        onChange={e => {
                          const next = e.target.value
                          if (providers[next]?.enabled === false) return
                          setHostedProvider(next)
                          setHostedModel(providers[next]?.models?.[0] ?? '')
                        }}
                        className={s.input}
                      >
                        <option value="">Select a provider...</option>
                        {Object.entries(providers).map(([key, p]) => (
                          <option key={key} value={key} disabled={p.enabled === false}>
                            {p.label}{p.enabled === false ? ' 🔒 (coming soon)' : ''}
                          </option>
                        ))}
                      </select>
                    </div>
                    {hostedProvider && (providers[hostedProvider]?.models?.length ?? 0) > 1 && (
                      <div className={s.formField}>
                        <label>Model *</label>
                        <select value={hostedModel} onChange={e => setHostedModel(e.target.value)} className={s.input}>
                          <option value="">Select a model...</option>
                          {providers[hostedProvider]?.models.map(m => <option key={m} value={m}>{m}</option>)}
                        </select>
                      </div>
                    )}
                    {useOwnKey && (
                      <div className={s.formField}>
                        <label>API Key *</label>
                        {existing?.hasApiKey && !replaceKey ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                            <span style={{ fontSize: '0.82rem', color: 'var(--success)', fontWeight: 600 }}>✓ API key configured</span>
                            <button onClick={() => setReplaceKey(true)} className={s.ghostBtnSm}>Replace</button>
                          </div>
                        ) : (
                          <>
                            <input type="password" value={apiKey} onChange={e => setApiKey(e.target.value)} placeholder={`Paste your ${PROVIDERS[hostedProvider]?.label ?? 'provider'} API key`} className={s.input} style={{ fontFamily: 'monospace' }} />
                            <span className={s.fieldHint}>Stored encrypted at rest. Never sent to the frontend.</span>
                          </>
                        )}
                      </div>
                    )}
                    <div className={s.formField}>
                      <label>System Prompt *</label>
                      <textarea
                        value={systemPrompt}
                        onChange={e => { setSystemPrompt(e.target.value); if (e.target.value.trim()) setPromptError(null) }}
                        placeholder="You are a helpful expert in... Answer questions clearly and concisely."
                        className={s.input}
                        rows={6}
                        style={{ resize: 'vertical', fontFamily: 'inherit', fontSize: '0.82rem', borderColor: promptError ? 'var(--error)' : undefined }}
                      />
                      {promptError && <span style={{ fontSize: '0.72rem', color: 'var(--error)' }}>{promptError}</span>}
                      {!promptError && <span className={s.fieldHint}>Instructions sent to the model before every discussion.</span>}
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {activeTab === 'knowledge' && agentRecord && agentType === 'hosted' && (
            <KnowledgeBaseSection agentId={agentRecord.id} />
          )}

          {activeTab === 'billing' && agentRecord && (
            <div style={{ padding: '1.25rem 1.5rem' }}>
              <AgentBillingPanel agentId={agentRecord.id} agentType={agentType} />
            </div>
          )}

          {activeTab === 'sandbox' && agentRecord && agentType === 'external' && (
            <SandboxTab
              agentId={agentRecord.id}
              endpoint={endpoint.trim() || null}
              signingSecret={createdAgent?.signingSecret ?? null}
              apiKeyPrefix={apiKeyPrefix}
            />
          )}

          {activeTab === 'invocations' && agentRecord && (
            <InvocationsTab agentId={agentRecord.id} />
          )}
        </div>

        {/* Only external agents show the endpoint-specific error inline next to
            the URL field instead — scoping the suppression to that agent type
            (rather than matching "endpoint"/"url" anywhere in the message)
            means a hosted-agent save error is never silently swallowed just
            because its wording happens to include either word. */}
        {formError && !(agentType === 'external' && (formError.toLowerCase().includes('endpoint') || formError.toLowerCase().includes('url'))) && (
          <div style={{ padding: '0 1.5rem', marginTop: '0.5rem', fontSize: '0.74rem', color: 'var(--error)' }}>{formError}</div>
        )}

        {/* Footer — context-sensitive: Details tab saves the form; once the
            agent exists, Knowledge Base/Sandbox/Invocations tabs surface
            Submit for Review (draft, gated on the same knowledge-base rule
            as before) or Resubmit (rejected/withdrawn) instead, since
            there's nothing left to "save" on those tabs. */}
        <div className={s.modalActions}>
          {activeTab === 'details' ? (
            <>
              {saved && <span style={{ fontSize: '0.75rem', color: 'var(--success)', alignSelf: 'center', marginRight: 'auto' }}>✓ Saved</span>}
              <button onClick={onClose} className={s.ghostBtn}>Cancel</button>
              <button
                onClick={() => void handleSave()}
                disabled={isLoading || !name.trim() || !!nameFormatError || nameAvailable === false
                  || (agentType === 'external' && !endpoint.trim())
                  || (agentType === 'hosted' && (!hostedProvider || !hostedModel || (useOwnKey && !existing?.hasApiKey && !apiKey.trim())))
                  || (!!agentRecord && !dirty)}
                className={s.primaryBtn}
              >
                {isLoading ? 'Saving...' : agentRecord ? (dirty ? 'Save Changes' : 'Saved') : 'Register Agent →'}
              </button>
            </>
          ) : isDraftRecord ? (
            <>
              {!hasReady && (
                <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)', alignSelf: 'center' }}>
                  Upload at least one document to submit
                </span>
              )}
              <button onClick={onClose} className={s.ghostBtn}>Close</button>
              <button
                onClick={() => void handleFinalize()}
                disabled={!hasReady || finalizing || discarding}
                className={s.primaryBtn}
              >
                {finalizing ? 'Submitting…' : isAdmin ? 'Activate Agent →' : 'Submit for Review →'}
              </button>
            </>
          ) : canResubmit ? (
            <>
              <button onClick={onClose} className={s.ghostBtn}>Close</button>
              <button
                onClick={() => void handleResubmit()}
                disabled={resubmitting}
                className={s.primaryBtn}
              >
                {resubmitting ? 'Submitting…' : '🔄 Resubmit for Review →'}
              </button>
            </>
          ) : (
            <button onClick={onClose} className={s.ghostBtn}>Close</button>
          )}
        </div>
      </div>
    </div>
  )
}
